import { useEffect, useState } from "react";
import axios from "axios";
import { adminErrorDetail } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bt_admin_pw";

const STATUS_LABELS = {
  active: "Active",
  completed: "Completed",
  unsubscribed: "Unsubscribed",
  skipped_stale: "Skipped (stale)",
  excluded_precutoff: "Excluded (pre-cutoff)",
  hard_failed: "Hard failed",
};
const STATUS_ORDER = ["active", "completed", "unsubscribed", "skipped_stale", "excluded_precutoff", "hard_failed"];
const DAY_BY_NUMBER = { 1: 1, 2: 5, 3: 10, 4: 15, 5: 20, 6: 25, 7: 30, 8: 40 };

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const Flag = ({ on, label, warn }) => (
  <span
    className={`label-mono px-2 py-0.5 border ${
      on ? (warn ? "border-red-200 text-red-600" : "border-hairline text-ink") : "border-hairline text-muted"
    }`}
  >
    {label} {on ? "on" : "off"}
  </span>
);

export default function AdminSequence({ csrf }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [delay, setDelay] = useState(null);
  const [delayMsg, setDelayMsg] = useState("");
  const [savingDelay, setSavingDelay] = useState(false);

  useEffect(() => {
    const headers = { "X-Admin-Password": sessionStorage.getItem(STORAGE_KEY) || "" };
    axios
      .get(`${API}/admin/sequence`, { headers })
      .then((r) => setData(r.data))
      .catch(() => setError("Could not load sequence data."));
    axios
      .get(`${API}/admin/sequence/send-delay`, { headers })
      .then((r) => setDelay(r.data))
      .catch(() => {});
  }, []);

  const saveDelay = async () => {
    setSavingDelay(true);
    setDelayMsg("");
    try {
      const r = await axios.put(
        `${API}/admin/sequence/send-delay`,
        { min_seconds: Number(delay.min_seconds), max_seconds: Number(delay.max_seconds) },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setDelay({ min_seconds: r.data.min_seconds, max_seconds: r.data.max_seconds });
      setDelayMsg("Saved");
    } catch (e) {
      setDelayMsg(adminErrorDetail(e, "Could not save. Log out and back in, then try again."));
    } finally {
      setSavingDelay(false);
    }
  };

  if (error) return <p className="text-sm text-red-600" data-testid="sequence-error">{error}</p>;
  if (!data) return <p className="text-muted" data-testid="sequence-loading">Loading…</p>;

  const cfg = data.config || {};
  const lastRun = data.last_run;
  const summary = lastRun?.summary || {};
  const missing = [];
  if (!cfg.start_date) missing.push("SEQUENCE_START_DATE");
  if (!cfg.postal_address_set) missing.push("SEQUENCE_POSTAL_ADDRESS");
  const postalIsPlaceholder = (cfg.postal_address || "").includes("PLACEHOLDER");

  return (
    <div data-testid="table-sequence">
      <h2 className="font-display text-3xl md:text-4xl mb-8">Sequence</h2>

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="sequence-flags">
        <Flag on={cfg.enabled} label="Sending" />
        <Flag on={cfg.dry_run} label="Dry run" />
        <Flag on={cfg.test_mode} label="Test mode" warn />
        <span className="label-mono text-muted">
          Cutoff {cfg.start_date || "not set"} · cap {cfg.max_sends_per_run}/run
        </span>
      </div>

      {delay && (
        <div className="mb-8 border border-hairline p-4 max-w-md" data-testid="sequence-send-delay">
          <p className="label-mono text-muted mb-1">Send spacing (random, per send)</p>
          <p className="text-sm text-muted mb-3">Random pause between individual sends within a run. Higher feels more human; lower drains a backlog faster.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex items-baseline gap-2 label-mono text-muted">
              Min
              <input
                type="number"
                min="0"
                max="600"
                value={delay.min_seconds}
                onChange={(e) => setDelay((d) => ({ ...d, min_seconds: e.target.value }))}
                className="w-20 border border-hairline px-2 py-1 text-right font-display text-lg"
                data-testid="send-delay-min"
              />
            </label>
            <label className="flex items-baseline gap-2 label-mono text-muted">
              Max
              <input
                type="number"
                min="0"
                max="600"
                value={delay.max_seconds}
                onChange={(e) => setDelay((d) => ({ ...d, max_seconds: e.target.value }))}
                className="w-20 border border-hairline px-2 py-1 text-right font-display text-lg"
                data-testid="send-delay-max"
              />
            </label>
            <span className="label-mono text-muted">seconds</span>
            <button
              onClick={saveDelay}
              disabled={savingDelay}
              className="label-mono bg-ink text-white px-3 py-1.5 hover:bg-leaf transition-colors disabled:opacity-40"
              data-testid="send-delay-save"
            >
              {savingDelay ? "Saving…" : "Save"}
            </button>
            {delayMsg && (
              <span className={`label-mono ${delayMsg === "Saved" ? "text-leaf" : "text-red-600"}`} data-testid="send-delay-msg">{delayMsg}</span>
            )}
          </div>
        </div>
      )}

      {missing.length > 0 && (
        <p className="mb-4 text-sm text-red-600" data-testid="sequence-missing-env">
          Required before the job can send: {missing.join(", ")}. The run endpoint refuses with a 503 while unset.
        </p>
      )}

      {postalIsPlaceholder && (
        <p className="mb-8 text-sm text-red-600" data-testid="sequence-postal-placeholder">
          Footer postal address is still a placeholder: {cfg.postal_address}
        </p>
      )}

      <div className="border-t border-hairline divide-y divide-hairline mb-12">
        <div className="py-3 label-mono text-muted">Leads by status · {data.total_in_sequence} tracked</div>
        {STATUS_ORDER.filter((s) => data.by_status?.[s]).map((s) => (
          <div key={s} className="py-3 flex items-baseline justify-between" data-testid={`sequence-status-${s}`}>
            <span className="text-sm text-ink">{STATUS_LABELS[s] || s}</span>
            <span className="font-display text-2xl">{data.by_status[s]}</span>
          </div>
        ))}
        {Object.keys(data.by_status || {}).length === 0 && (
          <div className="py-4 text-muted text-sm" data-testid="sequence-status-empty">No leads evaluated yet.</div>
        )}
      </div>

      <h3 className="label-mono text-muted mb-4">Sends by email</h3>
      <div className="border-t border-hairline divide-y divide-hairline mb-12">
        <div className="py-3 grid grid-cols-12 gap-4">
          <span className="col-span-4 label-mono text-muted">Email</span>
          <span className="col-span-4 label-mono text-muted text-right">Sent</span>
          <span className="col-span-4 label-mono text-muted text-right">Suppressed</span>
        </div>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
          const row = data.per_email_number?.[String(n)] || { sent: 0, suppressed: 0 };
          return (
            <div key={n} className="py-3 grid grid-cols-12 gap-4 items-baseline" data-testid={`sequence-email-row-${n}`}>
              <span className="col-span-4 text-sm text-ink">
                Email {n}{" "}
                <span className="label-mono text-muted">day {DAY_BY_NUMBER[n]}</span>
              </span>
              <span className="col-span-4 font-display text-xl text-right" data-testid={`sequence-email-sent-${n}`}>{row.sent}</span>
              <span className="col-span-4 font-display text-xl text-right text-muted" data-testid={`sequence-email-suppressed-${n}`}>{row.suppressed}</span>
            </div>
          );
        })}
      </div>

      <h3 className="label-mono text-muted mb-4">Last run</h3>
      {data.stale_run_warning && (
        <p className="mb-4 text-sm text-red-600" data-testid="sequence-stale-run-warning">
          No successful live run in the last 36 hours{data.last_ok_run_at ? ` — last was ${fmt(data.last_ok_run_at)}` : " — none recorded yet"}.
        </p>
      )}
      {!lastRun ? (
        <p className="text-muted text-sm mb-12" data-testid="sequence-no-runs">The job has never run.</p>
      ) : (
        <div className="border-t border-hairline divide-y divide-hairline mb-12" data-testid="sequence-last-run">
          {[
            ["Ran at", fmt(lastRun.run_at)],
            ["Result", lastRun.ok ? (lastRun.dry_run ? "OK (dry run)" : "OK") : `Failed — ${lastRun.error || "unknown"}`],
            ["Leads evaluated", summary.leads_evaluated ?? "—"],
            ["Emails sent", lastRun.emails_sent ?? 0],
            ["Waiting for their day", summary.waiting ?? "—"],
            ["Held by the gap rule", (summary.held_by_gap || []).length],
            ["Cap hit", summary.cap_hit ? "Yes" : "No"],
            ["Skips", Object.entries(summary.skips || {}).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ") || "none"],
          ].map(([k, v]) => (
            <div key={k} className="py-3 grid grid-cols-12 gap-4 items-baseline">
              <span className="col-span-5 md:col-span-3 label-mono text-muted">{k}</span>
              <span className="col-span-7 md:col-span-9 text-sm text-ink break-words">{v}</span>
            </div>
          ))}
        </div>
      )}

      <h3 className="label-mono text-muted mb-4">
        Skipped as stale · {data.stale_leads.length} never entered the sequence
      </h3>
      <p className="text-sm text-muted mb-4 max-w-2xl">
        Email 1 opens on the book having arrived yesterday, so it is never sent late. These leads are
        reachable by broadcast only.
      </p>
      {data.stale_leads.length === 0 ? (
        <p className="text-muted text-sm mb-12" data-testid="sequence-stale-empty">None.</p>
      ) : (
        <>
          <div className="border-t border-hairline divide-y divide-hairline mb-4">
            <div className="py-3 hidden md:grid grid-cols-12 gap-4">
              <span className="col-span-5 label-mono text-muted">Email</span>
              <span className="col-span-4 label-mono text-muted">Book sent</span>
              <span className="col-span-3 label-mono text-muted">During outage</span>
            </div>
            {data.stale_leads.map((l) => (
              <div key={l.lead_id} className="py-3 grid grid-cols-12 gap-4 items-baseline" data-testid={`sequence-stale-row-${l.lead_id}`}>
                <span className="col-span-12 md:col-span-5 text-sm text-ink break-all">{l.email}</span>
                <span className="col-span-6 md:col-span-4 label-mono text-muted">{fmt(l.book_sent_at)}</span>
                <span className="col-span-6 md:col-span-3 label-mono text-muted">{l.during_outage ? "Yes" : "No"}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => {
              const rows = [["email", "book_sent_at", "during_outage", "skip_reason"]].concat(
                data.stale_leads.map((l) => [l.email, l.book_sent_at || "", l.during_outage ? "yes" : "no", l.skip_reason || ""])
              );
              const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
              const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = "sequence-skipped-stale.csv";
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="label-mono border border-hairline text-ink px-3 py-1 mb-12 hover:bg-ink hover:text-white transition-colors"
            data-testid="sequence-stale-export"
          >
            Export CSV
          </button>
        </>
      )}

      <h3 className="label-mono text-muted mb-4">Hard failures</h3>
      {data.hard_failures.length === 0 ? (
        <p className="text-muted text-sm mb-12" data-testid="sequence-failures-empty">None.</p>
      ) : (
        <div className="border-t border-hairline divide-y divide-hairline mb-12">
          {data.hard_failures.map((f) => (
            <div key={f.lead_id} className="py-3 grid grid-cols-12 gap-4 items-baseline" data-testid={`sequence-failure-row-${f.lead_id}`}>
              <span className="col-span-12 md:col-span-4 text-sm text-ink break-all">{f.email}</span>
              <span className="col-span-3 md:col-span-2 label-mono text-muted">Email {f.email_number ?? "—"}</span>
              <span className="col-span-9 md:col-span-6 label-mono text-muted break-words">{f.last_error || "—"}</span>
            </div>
          ))}
        </div>
      )}

      <h3 className="label-mono text-muted mb-4">Most recent sends</h3>
      {data.recent_sends.length === 0 ? (
        <p className="text-muted text-sm" data-testid="sequence-recent-empty">Nothing sent yet.</p>
      ) : (
        <div className="border-t border-hairline divide-y divide-hairline">
          <div className="py-3 hidden md:grid grid-cols-12 gap-4">
            <span className="col-span-5 label-mono text-muted">Email</span>
            <span className="col-span-2 label-mono text-muted">Number</span>
            <span className="col-span-3 label-mono text-muted">Sent</span>
            <span className="col-span-2 label-mono text-muted">Suppressed</span>
          </div>
          {data.recent_sends.map((s, i) => (
            <div key={`${s.email}-${s.email_number}-${i}`} className="py-3 grid grid-cols-12 gap-4 items-baseline" data-testid={`sequence-send-row-${i}`}>
              <span className="col-span-12 md:col-span-5 text-sm text-ink break-all">{s.email}</span>
              <span className="col-span-4 md:col-span-2 label-mono text-muted">{s.email_number}</span>
              <span className="col-span-8 md:col-span-3 label-mono text-muted">{fmt(s.sent_at)}</span>
              <span className="col-span-12 md:col-span-2 label-mono text-muted">{s.suppressed ? "Yes" : "No"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
